import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Building, Phone, Mail, CheckCircle2, XCircle, Edit } from 'lucide-react';
import { toast } from 'react-toastify';
import SummaryApi from '../../../../common/SummaryApi';
import api from '../../../../common/apiClient';

const BranchView = () => {
  const { portalName, id } = useParams();
  const navigate = useNavigate();

  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBranchDetails = async () => {
      setLoading(true);
      try {
        const url = typeof SummaryApi.getBranchById.url === 'function'
          ? SummaryApi.getBranchById.url(id)
          : SummaryApi.getBranchById.url;

        const response = await api.get(url);
        if (response.data?.success) {
          setBranch(response.data.data);
        } else {
          toast.error(response.data?.message || 'Failed to load branch details');
        }
      } catch (error) {
        console.error('Error fetching branch details:', error);
        toast.error('Failed to load branch information');
      } finally {
        setLoading(false);
      }
    };

    fetchBranchDetails();
  }, [id]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-16">
        <Loader2 className="w-8 h-8 text-red-600 animate-spin mb-3" />
        <p className="text-sm text-gray-500">Loading branch details...</p>
      </div>
    );
  }

  if (!branch) {
    return (
      <div className="p-8 text-center bg-white rounded-xl shadow-sm border border-gray-200">
        <Building className="w-12 h-12 text-gray-300 mx-auto mb-2" />
        <h3 className="text-base font-bold text-gray-700">Branch not found</h3>
        <button
          onClick={() => navigate(`/${portalName}/home/settings/branches`)}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg text-xs font-semibold hover:bg-red-700 transition-colors"
        >
          Back to List
        </button>
      </div>
    );
  }

  const manager = branch.branchManagerId;
  const managerName = manager 
    ? (manager.fullName || `${manager.firstName || ''} ${manager.lastName || ''}`.trim() || manager.email) 
    : 'Not Assigned';

  return (
    <div className="p-2 space-y-3">
      {/* Top Header Card */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-xl shadow-xs border border-gray-200">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/${portalName}/home/settings/branches`)}
            className="p-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
            title="Back to List"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>

          <div className="w-14 h-14 bg-red-50 text-red-600 rounded-xl flex items-center justify-center text-xl font-bold uppercase shrink-0">
            {branch.branchName?.slice(0, 2)}
          </div>

          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">{branch.branchName}</h1>
              {branch.isMainBranch && (
                <span className="px-2.5 py-0.5 text-xs font-bold bg-amber-100 text-amber-800 rounded-full">
                  Head Office
                </span>
              )}
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                branch.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                {branch.isActive ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                {branch.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <p className="text-xs text-gray-500 font-mono mt-0.5">
              Code: <strong className="text-gray-700">{branch.branchCode}</strong>
            </p>
          </div>
        </div>

        <button
          onClick={() => navigate(`/${portalName}/home/settings/branches/edit/${branch._id}`)}
          className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium shadow-xs transition-colors shrink-0"
        >
          <Edit className="w-4 h-4" />
          <span>Edit Branch</span>
        </button>
      </div>

      {/* Grid Information */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Main Details */}
        <div className="md:col-span-2 bg-white p-5 rounded-xl shadow-xs border border-gray-200 space-y-4">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide pb-2 border-b border-gray-100">
            Branch Overview
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-xs text-gray-400 block uppercase">Branch Manager / In-Charge</span>
              <span className="font-semibold text-gray-800">{managerName}</span>
            </div>

            <div>
              <span className="text-xs text-gray-400 block uppercase">GSTIN Number</span>
              <span className="font-mono font-semibold text-gray-800">{branch.gstNumber || 'N/A'}</span>
            </div>

            <div className="sm:col-span-2">
              <span className="text-xs text-gray-400 block uppercase mb-1">Mapped Divisions</span>
              <div className="flex flex-wrap gap-1.5">
                {branch.divisionIds?.length > 0 ? (
                  branch.divisionIds.map((div, i) => (
                    <span key={i} className="px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-md text-xs font-semibold">
                      {div.name || 'Division'}
                    </span>
                  ))
                ) : (
                  <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded">
                    All Divisions (Global Branch)
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-100">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">Address & Location</h3>
            <p className="text-sm text-gray-700 font-medium">
              {branch.address || 'No address provided'}, {branch.locality || ''}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {[branch.district, branch.state, branch.country, branch.pincode].filter(Boolean).join(' - ')}
            </p>
          </div>
        </div>

        {/* Contact Strip */}
        <div className="bg-white p-5 rounded-xl shadow-xs border border-gray-200 space-y-4">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide pb-2 border-b border-gray-100">
            Contact Channels
          </h2>

          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-gray-400 shrink-0" />
              <div>
                <span className="text-[11px] text-gray-400 block uppercase">Phone</span>
                <span className="font-semibold text-gray-800">{branch.contactPhone || 'N/A'}</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Mail className="w-4 h-4 text-gray-400 shrink-0" />
              <div>
                <span className="text-[11px] text-gray-400 block uppercase">Email</span>
                <span className="font-semibold text-gray-800 break-all">{branch.contactEmail || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Audit Footer */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 text-xs text-gray-500 flex flex-wrap items-center justify-between gap-2 shadow-xs">
        <span>Created: {new Date(branch.createdAt).toLocaleDateString()}</span>
        <span>Last Updated: {new Date(branch.updatedAt).toLocaleDateString()}</span>
      </div>
    </div>
  );
};

export default BranchView;